import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { dbSelect, dbDelete } from '../lib/api';
import { ArrowLeft, Trash2, Flag, ExternalLink } from 'lucide-react';

interface ProblemReport {
  id: string;
  project_id: string;
  user_id: string | null;
  description: string | null;
  page_url: string | null;
  created_at: string;
  projects: { name: string; public_id: string | null } | null;
  reporter_email?: string;
}

export function AdminProblemReportsPage() {
  const { user, session, loading: authLoading } = useAuth();
  const [reports, setReports] = useState<ProblemReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const ADMIN_EMAIL = 'a.burkov@gmail.com';

  useEffect(() => {
    document.title = 'Problem Reports - Admin - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  useEffect(() => {
    if (session && user?.email === ADMIN_EMAIL) {
      fetchReports();
    }
  }, [session, user]);

  const fetchReports = async () => {
    if (!session) return;
    setLoading(true);

    const { data, error } = await dbSelect<ProblemReport[]>('problem_reports', {
      token: session.access_token,
      select: 'id,project_id,user_id,description,page_url,created_at,projects(name,public_id)',
      order: 'created_at.desc',
    });

    if (!error && data) {
      // Fetch reporter emails for reports with user_id
      const userIds = [...new Set(data.filter(r => r.user_id).map(r => r.user_id!))];
      let emailMap: Record<string, string> = {};

      if (userIds.length > 0) {
        const { data: profiles } = await dbSelect<{ id: string; email: string }[]>('profiles', {
          token: session.access_token,
          select: 'id,email',
          filters: { id: `in.(${userIds.join(',')})` },
        });
        if (profiles) {
          emailMap = Object.fromEntries(profiles.map(p => [p.id, p.email]));
        }
      }

      setReports(data.map(r => ({
        ...r,
        reporter_email: r.user_id ? emailMap[r.user_id] : undefined,
      })));
    }

    setLoading(false);
  };

  const handleDismiss = async (reportId: string) => {
    if (!session) return;
    setDeletingId(reportId);

    const { error } = await dbDelete('problem_reports', { id: `eq.${reportId}` }, {
      token: session.access_token,
    });

    if (!error) {
      setReports(prev => prev.filter(r => r.id !== reportId));
    }

    setDeletingId(null);
  };

  if (authLoading) return <PageLoader />;
  if (!user || user.email !== ADMIN_EMAIL) return <Navigate to="/dashboard" replace />;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/admin" className="btn btn-ghost p-2">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/10 text-red-500">
            <Flag className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Problem Reports
            </h1>
            <p className="text-theme-text-secondary">
              {reports.length} report{reports.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : reports.length === 0 ? (
          <div className="card p-8 text-center text-theme-text-muted">
            No problem reports yet.
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <div key={report.id} className="card p-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-theme-text-muted">
                      {new Date(report.created_at).toLocaleDateString()} {new Date(report.created_at).toLocaleTimeString()}
                    </span>
                    {report.reporter_email && (
                      <span className="text-xs text-theme-text-secondary">
                        by {report.reporter_email}
                      </span>
                    )}
                    {!report.user_id && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-theme-surface text-theme-text-muted">
                        anonymous
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    {report.projects ? (
                      <>
                        <Link
                          to={`/project/${report.project_id}`}
                          className="text-sm font-medium text-accent-500 hover:underline"
                        >
                          {report.projects.name}
                        </Link>
                        {report.projects.public_id && (
                          <Link
                            to={`/p/${report.projects.public_id}`}
                            className="text-xs text-theme-text-muted hover:text-accent-500 flex items-center gap-0.5"
                            title="Public page"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-theme-text-muted italic">Deleted project</span>
                    )}
                  </div>
                  {report.description && (
                    <p className="text-sm text-theme-text-secondary mt-1 whitespace-pre-wrap">
                      {report.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleDismiss(report.id)}
                  disabled={deletingId === report.id}
                  className="shrink-0 p-2 rounded-lg text-theme-text-muted hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  title="Dismiss report"
                >
                  {deletingId === report.id ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
